import { useEffect, useRef, useState } from "react";

/**
 * An alternative to useEffect that makes sure to only run once per key, even in strict mode.
 */
export const useDedupedEffect = (
  effectFn: (isInitial: boolean) => (() => void) | void,
  key: string | number | symbol,
) => {
  const ref = useRef({
    key,
    setupHasRun: false,
    cleanupFn: null as (() => void) | null,
  });

  if (key !== ref.current.key) {
    if (ref.current.cleanupFn) {
      ref.current.cleanupFn();
    }

    ref.current = {
      key,
      setupHasRun: false,
      cleanupFn: null,
    };
  }

  const [inited, setInited] = useState(false);

  useEffect(() => {
    if (inited) {
      return () => {
        if (ref.current.cleanupFn) {
          ref.current.cleanupFn();
        }
      };
    }
    setInited(true);
  }, [inited]);

  useEffect(() => {
    if (ref.current.setupHasRun) return;

    const result = effectFn(!inited);
    if (typeof result === "function") {
      ref.current.cleanupFn = result;
    }
    ref.current.setupHasRun = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};
